<template>
  <div>
    <div v-if="!loading">
      <div v-if="quoteRequest" class="flex flex-col">
        <div class="card">
          <div class="mt-2 text-sm">
            <p class="font-medium">Cliente</p>
            <router-link
              :to="{
                name: 'clientsDetail',
                params: { id: quoteRequest.client.id },
              }"
              class="underline system-button"
              >{{ quoteRequest.client.name }}
              {{ quoteRequest.client.last_name }}</router-link
            >
          </div>

          <p class="font-medium mt-2">Mascotas</p>
          <p class="text-content-400">Selecciona una dieta para cada mascota</p>

          <div class="flex flex-wrap">
            <div
              v-for="pet in quoteRequest.pets"
              :key="pet.id"
              class="card-bordered mt-2 sm:mr-2"
            >
              <router-link
                :to="{ name: 'petsDetail', params: { id: pet.id } }"
                class="underline system-button"
                >{{ pet.name }}</router-link
              >

              <p class="mt-2">Dieta</p>

              <div v-if="pet.diets.length === 0">
                <p class="text-sm font-medium">
                  No existe una dieta para esta mascota.
                </p>

                <router-link
                  :to="{ name: 'petsDetail', params: { id: pet.id } }"
                  class="secondary-button mt-2 block"
                  >Crear Dieta</router-link
                >
              </div>

              <div v-else class="flex flex-wrap space-x-4>">
                <div class="card-option-wrapper">
                  <div
                    v-for="diet in pet.diets"
                    :key="diet.id"
                    class="mt-2 card-option text-left"
                    :class="[
                      {
                        'selected-border': form.pets[`${pet.id}`] === diet.id,
                      },
                    ]"
                    @click.prevent="selectOption(pet.id, diet.id)"
                  >
                    <p class="text-sm font-medium">ID: #{{ diet.id }}</p>

                    <div class="mt-2 text-sm">
                      <p class="font-medium">Nombre de dieta</p>
                      <p>{{ diet.name }}</p>
                    </div>

                    <div class="mt-2 text-sm">
                      <p class="font-medium">Precio por semana</p>
                      <p>{{ integerToCurrency(diet.weekly_price) }}</p>
                    </div>

                    <div class="mt-2 text-sm">
                      <p class="font-medium">Cantidad total</p>
                      <p>{{ diet.total_quantity }}g</p>
                    </div>
                  </div>
                </div>
                <validation-errors
                  :errors="errorFor('food_type_id')"
                ></validation-errors>

                <!-- <diets :diets="pet.diets" class="mt-2" /> -->
              </div>
            </div>
          </div>

          <form @submit.prevent="createQuote">
            <div class="flex items-center mt-2">
              <input
                id="courtesy"
                type="checkbox"
                class="h-5 w-5"
                v-model="form.courtesy"
              />
              <span for="courtesy" class="mx-2">¿Es cortesía?</span>
              <tooltip message="Si seleccionas este campo no se cobrará el plan al cliente y se activará inmediatamente." position="right" />
            </div>

            <div v-if="form.courtesy" class="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p class="font-medium text-purple-800 text-sm mb-2">Configuración de cortesía</p>

              <div class="mt-2">
                <label for="delivery_frequency" class="text-sm font-medium">Frecuencia de entrega</label>
                <select
                  id="delivery_frequency"
                  v-model="form.delivery_frequency_id"
                  class="w-full mt-1"
                >
                  <option :value="null">Seleccionar frecuencia</option>
                  <option
                    v-for="freq in deliveryFrequencies"
                    :key="freq.id"
                    :value="freq.id"
                  >
                    {{ freq.name }} - {{ freq.weeks }} {{ freq.weeks === 1 ? 'semana' : 'semanas' }}
                  </option>
                </select>
              </div>

              <div class="flex items-center mt-3">
                <input
                  id="delivery_enabled"
                  type="checkbox"
                  class="h-5 w-5"
                  v-model="form.delivery_enabled"
                />
                <span for="delivery_enabled" class="mx-2 text-sm">Generar entregas</span>
                <tooltip message="Si desactivas esta opción, no se generarán entregas para este plan." position="right" />
              </div>
            </div>

            <primary-button
              class="mx-0 mt-8"
              :loading="loadingCreate"
              type="submit"
              >{{ form.courtesy ? 'Crear y activar plan de cortesía' : 'Cotizar Plan' }}</primary-button
            >
          </form>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import validationErrors from "@/mixins/validationErrors";
import Diet from "@/components/models/Diet";
import { is422 } from "@/utils/response";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";

export default {
  name: "Quote Request Detail",

  components: {
    Diet,
  },

  data() {
    return {
      quoteRequest: null,
      loading: false,
      loadingCreate: false,
      deliveryFrequencies: [],
      form: {
        diet_ids: [],
        quote_request_id: null,
        pets: {},
        courtesy: false,
        delivery_frequency_id: null,
        delivery_enabled: true,
      },
      editShippingInfo: false,
      shippingRequired: false,
    };
  },

  mixins: [deleteMixin, validationErrors],

  created() {
    this.$store.commit("setSelectedTab", "quotes");

    this.loadQuoteRequest();
    this.loadFrequencies();
  },

  methods: {
    formatDatetime,

    integerToCurrency,

    async loadFrequencies() {
      try {
        const response = await api.resources.getFrequencies();
        this.deliveryFrequencies = response.delivery_frequencies;
      } catch (error) {
        this.$store.dispatch("errorSnack", "Error al cargar frecuencias");
      }
    },

    async loadQuoteRequest() {
      this.loading = true;

      try {
        this.quoteRequest = await api.quoteRequests.getById(
          this.$route.params.id
        );

        this.quoteRequest.pets.forEach((pet) => {
          this.form.pets[`${pet.id}`] = null;
          pet.diets.forEach((diet) => {
            diet.collapsed = true;
          });
        });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "quotesIndex" });
      }

      this.loading = false;
    },

    selectOption(petId, dietId) {
      if (this.form.pets[`${petId}`] === dietId) {
        this.form.pets[`${petId}`] = null;
      } else {
        this.form.pets[`${petId}`] = dietId;
      }
    },

    async createQuote() {
      const hasNullValues = Object.values(this.form.pets).some(
        (value) => value === null
      );

      if (hasNullValues) {
        this.$store.dispatch(
          "errorSnack",
          "Cada mascota debe tener una dieta seleccionada."
        );
        return;
      }

      if (this.form.courtesy && this.form.delivery_enabled && !this.form.delivery_frequency_id) {
        this.$store.dispatch(
          "errorSnack",
          "Selecciona una frecuencia de entrega para el plan de cortesía."
        );
        return;
      }

      this.form.diet_ids = Object.values(this.form.pets);

      this.form.quote_request_id = this.quoteRequest.id;

      this.loadingCreate = true;

      try {
        const response = await api.quotes.create(this.form);
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
        this.$router.push({ name: "quotesIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingCreate = false;
    },
  },
};
</script>